import React, { useMemo } from 'react';
import Banner from '../../../../../components/Banner';
import IconAiArrow from '../../../../../components/IconWrapper/arrow-ai';
import usePlatform from '../../../../../hooks/usePlatform';
import { getBannerOperationExtraAttr } from '../../../../../scripts/utils';
import './index.scss';
import '../../../../../components/AIP/aip-banner-wrap.scss';

type NoteTip = { value?: string; label?: string; img?: string };

type FeatureItem = {
  title: string;
  description: string;
};

const retailSectionDiagram1 = '/images/aip/Retail/global-inventory-hub.png';
const retailSectionDiagram2 = '/images/aip/Retail/unified-order-center.png';
const retailSectionDiagram3 = '/images/aip/Retail/integrated-customer-engine.png';
const retailSectionDiagram4 = '/images/aip/Retail/intelligent-decision-system-v2.png';
const case01 = '/images/aip/Retail/case-01.png';
const bannerBg = '/images/aip/Retail/retail-banner-bg.svg';

type FeatureSectionData = {
  title: string;
  subtitle: string;
  previewAlt: string;
  previewImage: string;
  items: FeatureItem[];
};

const featureSections: FeatureSectionData[] = [
  {
    title: '全局库存中枢',
    subtitle: '全渠道的“资源调度器”',
    previewAlt: '全局库存中枢示意图',
    previewImage: retailSectionDiagram1,
    items: [
      {
        title: '多层次库存视图',
        description: '清晰管理实物、逻辑、共享、渠道预占四层库存状态，实现从总盘到渠道端的精细化管控。'
      },
      {
        title: '智能共享与独占策略',
        description: '基于渠道优先级、区域仓配等规则，灵活配置库存共享池与独占策略，最大化商品售罄率。'
      },
      {
        title: '自动化调拨与补给',
        description: '根据各节点销售与库存状况，自动生成店间调拨或仓库补货建议，确保库存分布最优。'
      },
      {
        title: '全渠道实时可视',
        description: '提供跨所有仓库、门店、线上渠道的统一实时库存视图，任何移动与变更即时同步。'
      }
    ]
  },
  {
    title: '统一订单中心',
    subtitle: '全链路的“履约智能体”',
    previewAlt: '统一订单中心示意图',
    previewImage: retailSectionDiagram2,
    items: [
      {
        title: '全渠道订单汇聚',
        description:
          '自动接收并汇聚来自门店POS、自营平台、第三方渠道（天猫/京东/抖音等）的所有订单，形成统一订单池。'
      },
      {
        title: '智能履约路由',
        description: '基于预设规则（就近发货、成本最优、时效最快），自动为订单分配合适的发货仓或门店，实现高效履约。'
      },
      {
        title: '一体化作业与交付',
        description:
          '支持统一打单、拣货、打包、发货与售后流程，大幅降低仓库复杂度，为消费者提供一致、可追踪的交付体验。'
      },
      {
        title: '多元化服务支持',
        description: '无缝支持到店自提、门店发货、即时配送等多场景履约模式，满足全渠道零售需求。'
      }
    ]
  },
  {
    title: '一体化客户引擎',
    subtitle: '全景的“体验连接器”',
    previewAlt: '一体化客户引擎示意图',
    previewImage: retailSectionDiagram3,
    items: [
      {
        title: '统一会员中心',
        description: '打通各渠道会员身份，构建360°统一会员视图，实现积分、等级、权益的全渠道通认通兑。'
      },
      {
        title: '集中促销引擎',
        description:
          '支持创建复杂的全渠道或分渠道促销活动（满减、折扣、会员价、券等），确保各触点价格与优惠策略统一。'
      },
      {
        title: '无缝客户旅程',
        description:
          '支持线上下单线下自提、线下体验线上复购等场景，确保客户在任何触点都能享受连贯的优惠与服务。'
      },
      {
        title: '精准营销触达',
        description: '基于会员画像与行为数据，进行个性化推荐与精准营销，提升复购与客户终身价值。'
      }
    ]
  },
  {
    title: '智能决策系统',
    subtitle: '业务的“数据驾驶舱”',
    previewAlt: '智能决策系统示意图',
    previewImage: retailSectionDiagram4,
    items: [
      {
        title: 'AI销量智能预测',
        description: '基于历史销售、门店特性、季节趋势、促销计划多维度因素，预测各门店/仓库的未来销量。'
      },
      {
        title: '自动化补货建议',
        description:
          '为每个门店或前置仓自动生成科学的补货建议，包括SKU、数量与预期到货时间，变“经验补货”为“数据补货”。'
      },
      {
        title: '实时经营仪表盘',
        description: '集中呈现全渠道销售额、库存周转、订单履行率、会员活跃度等核心指标，一屏纵览业务健康度。'
      },
      {
        title: '深度经营分析',
        description: '支持商品贡献、渠道效能、促销ROI等多维度分析，精准定位增长机会与优化点，驱动科学决策。'
      }
    ]
  }
];

function FeatureSection({ section }: { section: FeatureSectionData }) {
  return (
    <section className="section">
      <div className="container">
        <h2 className="section-title">{section.title}</h2>
        <p className="section-subtitle feature-subtitle">{section.subtitle}</p>
        <div className="feature-split">
          <div className="feature-list">
            {section.items.map((item, index) => (
              <article key={`${section.title}-${item.title}`} className="feature-item feature-item-static">
                <div className="feature-head">
                  <span className="feature-title">{item.title}</span>
                  <span className="feature-index">#{index + 1}</span>
                </div>
                <p className="feature-desc">{item.description}</p>
              </article>
            ))}
          </div>
          <div className="feature-preview">
            <div className="feature-placeholder">
              <img
                className="feature-preview-image"
                src={section.previewImage}
                alt={section.previewAlt}
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Retail() {
  const { isPc } = usePlatform() || {};

  const bannerConfig = useMemo(() => {
    const src = bannerBg;

    const note =
      '通过一体化整合与智能化决策，解决企业多渠道运营中面临的库存分散、体验割裂、运营低效等核心瓶颈，构建“一盘货卖全渠道、一套系统管全场景”的数字化运营体系，从而提升周转、优化体验、实现精准增长。';

    const noteTips: NoteTip[] = [
      { value: '+30%', label: '库存周转效率' },
      { value: '+40%', label: '订单履约人效' },
      { value: '+25%', label: '会员全渠道复购率' }
    ];

    return {
      src,
      mobileSrc: bannerBg,
      title: '业务应用-零售',
      subheading: '',
      legend: '驱动企业全渠道业务增长',
      note,
      noteTips,
      forceShowNote: true,
      operation: [
        {
          text: '立即咨询',
          icon: <IconAiArrow />,
          extraAttr: getBannerOperationExtraAttr({
            jumpBO: { formBOId: 1, formBO: { id: 1 }, jumpContentType: 'FORM' }
          })
        }
      ]
    };
  }, []);

  return (
    <div className="retail-page">
      <div className="aip-banner-wrap retail-banner">
        <Banner
          showAdvantage={false}
          covered
          useArrow={false}
          useDots={false}
          size="large"
          type={'BANNER_TYPE.CAROUSEL'}
          name={typeof bannerConfig.title === 'string' ? bannerConfig.title : undefined}
          height={isPc ? 700 : 600}
          config={bannerConfig as any}
        />
      </div>

      <main>
        {featureSections.map((section) => (
          <FeatureSection key={section.title} section={section} />
        ))}

        {/* 行业实践案例 - 暂时隐藏 */}
        {/* <section className="section" id="cases">
          <div className="container">
            <h2 className="section-title">行业实践案例</h2>
            <p className="section-subtitle feature-subtitle">智慧零售运营中枢实践案例</p>
            <div className="cases">
              <a className="case-link" href="https://terminus.io/case-detail/2009001" target="_blank" rel="noopener noreferrer">
                <article className="case-card">
                  <span className="case-badge">案例 01</span>
                  <div className="case-body">
                    <h3 className="case-title">智慧零售全渠道业务增长实践</h3>
                    <p className="case-desc">
                      通过一盘货、统一订单中心和客户引擎协同联动，帮助企业提升库存周转效率、履约人效与会员复购表现。
                    </p>
                    <div className="case-illustration">
                      <img className="case-illustration-img" src={case01} alt="零售案例配图" loading="lazy" />
                    </div>
                  </div>
                </article>
              </a>
            </div>
          </div>
        </section> */}
      </main>
    </div>
  );
}

